import { type BoundingBox, csCore, csTools, type Handle, type Point, type ToolImage } from './cstypes';
import { type Spacing, spacingFrom } from './geometry';

export { acuteAngle, midpoint, norm, physicalLength, physicalVector, type Spacing } from './geometry';

/** Padding around annotation text, same as the beta's drawTextBox. */
const TEXT_PADDING = 5;
const LINK_DASH = [2, 3];
const DEGREES = '°';
const DEFAULT_DECIMALS = 2;

/** Pixel spacing of an image: image plane metadata first (like the beta's LengthTool), else the image object. */
export function spacingOf(image: ToolImage): Spacing {
  const plane = csCore().metaData.get('imagePlaneModule', image.imageId) as
    | { rowPixelSpacing?: number; columnPixelSpacing?: number }
    | undefined;
  return spacingFrom(plane?.rowPixelSpacing || image.rowPixelSpacing, plane?.columnPixelSpacing || image.columnPixelSpacing);
}

/** Modality of the series an image belongs to ('' when unknown). */
export function modalityOf(imageId: string): string {
  const series = csCore().metaData.get('generalSeriesModule', imageId) as { modality?: string } | undefined;
  return series?.modality ?? '';
}

export function lengthUnit(spacing: Spacing): string {
  return spacing.calibrated ? 'mm' : 'pixels';
}

/** "12.34 mm" / "12.34 pixels" (IMEXHS units). */
export function formatLength(value: number, spacing: Spacing, decimals = DEFAULT_DECIMALS): string {
  return `${value.toFixed(decimals)} ${lengthUnit(spacing)}`;
}

export function formatAngle(value: number): string {
  return `${value.toFixed(DEFAULT_DECIMALS)}${DEGREES}`;
}

/** Distance (canvas px) from a canvas point to the canvas projection of image segment a–b. */
export function canvasDistanceToSegment(element: HTMLElement, a: Point, b: Point, coords: Point): number {
  const p = csCore().pixelToCanvas(element, a);
  const q = csCore().pixelToCanvas(element, b);
  const dx = q.x - p.x;
  const dy = q.y - p.y;
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((coords.x - p.x) * dx + (coords.y - p.y) * dy) / lengthSquared));
  return Math.hypot(coords.x - (p.x + t * dx), coords.y - (p.y + t * dy));
}

export function pointInBox(box: BoundingBox | undefined, coords: Point): boolean {
  if (!box) return false;
  return coords.x >= box.left && coords.x <= box.left + box.width && coords.y >= box.top && coords.y <= box.top + box.height;
}

/** A fresh 2D context in canvas (not image) coordinates, as the beta's getNewContext gives. */
export function canvasContext(context: CanvasRenderingContext2D): CanvasRenderingContext2D {
  const ctx = context.canvas.getContext('2d') as CanvasRenderingContext2D;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  return ctx;
}

export interface StrokeOptions {
  color: string;
  lineWidth?: number;
  dash?: number[];
}

/** Line between two image points. */
export function strokeLine(ctx: CanvasRenderingContext2D, element: HTMLElement, a: Point, b: Point, options: StrokeOptions): void {
  strokeCanvasLine(ctx, csCore().pixelToCanvas(element, a), csCore().pixelToCanvas(element, b), options);
}

/** Line between two canvas points. */
export function strokeCanvasLine(ctx: CanvasRenderingContext2D, a: Point, b: Point, options: StrokeOptions): void {
  ctx.save();
  ctx.beginPath();
  ctx.strokeStyle = options.color;
  ctx.lineWidth = options.lineWidth ?? csTools().toolStyle.getToolWidth();
  ctx.setLineDash(options.dash ?? []);
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  ctx.restore();
}

export interface TextBoxOptions {
  /** Box fill; the beta's default text background is transparent. */
  background?: string;
  /** Text colour when it differs from the outline colour (spine labels: black on yellow). */
  textColor?: string;
  centerX?: boolean;
  centerY?: boolean;
}

/** Multi-line text box at a canvas point; returns its bounding box (canvas coordinates). */
export function drawTextBox(ctx: CanvasRenderingContext2D, lines: string[], x: number, y: number, color: string, options: TextBoxOptions = {}): BoundingBox {
  const { textStyle } = csTools();
  const fontSize = textStyle.getFontSize();
  ctx.save();
  ctx.font = textStyle.getFont();
  const width = Math.max(0, ...lines.map((line) => ctx.measureText(line).width)) + TEXT_PADDING * 2;
  const height = TEXT_PADDING + lines.length * (fontSize + TEXT_PADDING);
  const box: BoundingBox = {
    left: options.centerX ? x - width / 2 : x,
    top: options.centerY ? y - height / 2 : y,
    width,
    height,
  };
  if (options.background) {
    ctx.fillStyle = options.background;
    ctx.fillRect(box.left, box.top, box.width, box.height);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.strokeRect(box.left, box.top, box.width, box.height);
  }
  ctx.fillStyle = options.textColor ?? color;
  ctx.textBaseline = 'top';
  lines.forEach((line, i) => ctx.fillText(line, box.left + TEXT_PADDING, box.top + TEXT_PADDING + i * (fontSize + TEXT_PADDING)));
  ctx.restore();
  return box;
}

/**
 * Text box tied to a measurement: drawn at the `textBox` handle (image coordinates); once the user
 * has dragged it away, a dashed link joins it to the closest anchor point.
 */
export function drawLinkedTextBox(
  ctx: CanvasRenderingContext2D,
  element: HTMLElement,
  textBox: Handle,
  lines: string[],
  anchors: Point[],
  color: string,
): void {
  const at = csCore().pixelToCanvas(element, textBox);
  const box = drawTextBox(ctx, lines, at.x + 10, at.y, color, { centerY: true });
  textBox.boundingBox = box;
  if (!textBox.hasMoved || anchors.length === 0) return;
  const canvasAnchors = anchors.map((p) => csCore().pixelToCanvas(element, p));
  const start = closest(canvasAnchors, at);
  const edges: Point[] = [
    { x: box.left + box.width / 2, y: box.top },
    { x: box.left, y: box.top + box.height / 2 },
    { x: box.left + box.width / 2, y: box.top + box.height },
    { x: box.left + box.width, y: box.top + box.height / 2 },
  ];
  strokeCanvasLine(ctx, start, closest(edges, start), { color, dash: LINK_DASH });
}

function closest(points: Point[], to: Point): Point {
  return points.reduce((best, p) => (Math.hypot(p.x - to.x, p.y - to.y) < Math.hypot(best.x - to.x, best.y - to.y) ? p : best));
}

/** A new, not-yet-moved text box handle (the same shape the beta's tools use). */
export function newTextBoxHandle(): Handle {
  return { x: 0, y: 0, active: false, hasMoved: false, movesIndependently: false, drawnIndependently: true, allowedOutsideImage: true, hasBoundingBox: true };
}

/** True when any placed handle (text boxes excluded) lies outside the image, like the beta's anyHandlesOutsideImage. */
export function anyHandleOutsideImage(image: ToolImage, handles: Record<string, Handle>): boolean {
  return Object.values(handles).some(
    (h) => !h.allowedOutsideImage && (h.x < 0 || h.y < 0 || h.x > image.columns || h.y > image.rows),
  );
}

/**
 * Runs a built-in renderer with every string it draws or measures passed through `translate`,
 * so the pinned beta tools can speak the IMEXHS labels without re-implementing their drawing.
 */
export function withTranslatedText(ctx: CanvasRenderingContext2D, translate: (text: string) => string, render: () => void): void {
  const fillText = ctx.fillText;
  const measureText = ctx.measureText;
  ctx.fillText = (text: string, x: number, y: number, maxWidth?: number) =>
    maxWidth === undefined ? fillText.call(ctx, translate(text), x, y) : fillText.call(ctx, translate(text), x, y, maxWidth);
  ctx.measureText = (text: string) => measureText.call(ctx, translate(text));
  try {
    render();
  } finally {
    Reflect.deleteProperty(ctx, 'fillText');
    Reflect.deleteProperty(ctx, 'measureText');
  }
}
